import { aggregateByOptions } from '../../duck';
import { Box, Button, HStack, IconButton, Text } from '@chakra-ui/react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
// eslint-disable-next-line @nrwl/nx/enforce-module-boundaries
import { Icon, Select } from '@lon/shared/components';
import { useGetIsLeaderSuitFlag } from '@lon/shared/hooks';
import { SsStandardsAggregationTypeEnum } from '@lon/shared/requests';

interface Props {
  total: number;
  itemsPerPage: number;
}

const StandardsPagination: React.FC<Props> = ({ total, itemsPerPage }) => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams({});

  const page = Number(searchParams.get('standards-page')) || 1;
  const numberOfPages = Math.ceil(total / itemsPerPage);
  const isDisableNext = page >= numberOfPages;
  const isDisabledPrev = page === 1;
  const [colElementWidth, setElementWidth] = useState<number>(265);

  const handleChange = (page: number) => {
    const params = Object.fromEntries(searchParams.entries());
    setSearchParams({ ...params, 'standards-page': page.toString() });
  };

  const options =
    numberOfPages === 0
      ? [{ label: 1, value: 1 }]
      : Array.from({ length: numberOfPages }).map((_, i) => ({
          label: i + 1,
          value: i + 1,
        }));

  const value = options.find((item) => item.value === page);
  const shown = Number(searchParams.get('shown'));
  const isLeader = useGetIsLeaderSuitFlag();
  const aggregateBy = searchParams.get(
    'aggregateBy'
  ) as SsStandardsAggregationTypeEnum;

  useEffect(() => {
    if (!aggregateBy) return;

    const id = aggregateByOptions[aggregateBy][1];
    const el = document.getElementById(id);

    if (!el) return;

    // Initial width
    setElementWidth(el.offsetWidth);

    // Listen for resize
    const resizeObserver = new ResizeObserver((entries) => {
      setElementWidth(el.offsetWidth);
    });

    resizeObserver.observe(el);

    return () => {
      resizeObserver.disconnect();
    };
  }, [aggregateBy]);

  useEffect(() => {
    if (shown > total) {
      const params = Object.fromEntries(searchParams.entries());
      setSearchParams({
        ...params,
        shown: total.toString(),
      });
    }
  }, [total]);

  const handleLoadMore = () => {
    const params = Object.fromEntries(searchParams.entries());
    setSearchParams({
      ...params,
      shown: Math.min(shown + itemsPerPage, total).toString(),
    });
  };

  if (isLeader && aggregateBy) {
    if (total === 0) {
      return null;
    }

    return (
      <HStack spacing={0} w="100%">
        <Box
          width={`${colElementWidth}px`}
          boxShadow="inset -1px -0px var(--chakra-colors-secondary-200)"
          height="100%"
        />
        <HStack
          spacing="6"
          justify="space-between"
          alignItems="center"
          px={8}
          py={2}
          width={`calc(100% - ${colElementWidth}px)`}
        >
          <Text
            variant="n3"
            color="primary.800"
            className="tr-skip"
            fontStyle="italic"
          >
            {/* Use translation if available, otherwise fallback */}
            {t('reportsDashboard.districtAssessmentsReports.showingData', {
              shown,
              total,
            }) || `${shown} of ${total} data is shown`}
          </Text>
          <Button
            size="sm"
            onClick={handleLoadMore}
            isDisabled={shown >= total}
            variant="ghost"
            colorScheme="blue"
            textDecoration="underline"
          >
            {t('reportsDashboard.districtAssessmentsReports.loadMore')}
          </Button>
        </HStack>
      </HStack>
    );
  }

  return (
    <HStack
      spacing="6"
      justify="space-between"
      w="fit-content"
      alignItems="center"
      py={4}
      px={8}
    >
      <HStack spacing="2">
        <IconButton
          size="sm"
          variant="empty"
          boxShadow="none"
          aria-label={t('pagination.firstPage')}
          className="tr-skip"
          onClick={() => {
            if (!isDisabledPrev) {
              handleChange(1);
            }
          }}
          isDisabled={isDisabledPrev}
          icon={<Icon name="arr-double-left-outlined" />}
        />
        <IconButton
          size="sm"
          variant="empty"
          boxShadow="none"
          aria-label={t('pagination.previousPage')}
          className="tr-skip"
          onClick={() => {
            if (!isDisabledPrev) {
              const currentPage = page - 1;
              handleChange(currentPage);
            }
          }}
          isDisabled={isDisabledPrev}
          icon={<Icon name="arr-left-outlined" />}
        />
      </HStack>
      <HStack spacing={4} align="center" alignSelf="normal">
        <Text variant="n3" color="primary.800" className="tr-skip">
          {t('reportsDashboard.page')}
        </Text>
        <Select
          menuPortalTarget={document.body}
          formControlProps={{ maxW: '65px' }}
          size="s"
          value={value}
          onChange={(item) => {
            handleChange(Number(item?.value));
          }}
          isDisabled={options.length === 0}
          options={options}
        />
      </HStack>
      <HStack spacing="2">
        <IconButton
          size="sm"
          variant="empty"
          boxShadow="none"
          transform="scaleX(-1)"
          aria-label={t('pagination.nextPage')}
          colorScheme="whiteAlpha"
          className="tr-skip"
          onClick={() => {
            if (!isDisableNext) {
              const currentPage = page + 1;

              handleChange(currentPage);
            }
          }}
          isDisabled={isDisableNext}
          icon={<Icon name="arr-left-outlined" />}
        />
        <IconButton
          size="sm"
          variant="empty"
          boxShadow="none"
          transform="scaleX(-1)"
          aria-label={t('pagination.lastPage')}
          colorScheme="whiteAlpha"
          className="tr-skip"
          onClick={() => {
            if (!isDisableNext) {
              handleChange(numberOfPages);
            }
          }}
          isDisabled={isDisableNext}
          icon={<Icon name="arr-double-left-outlined" />}
        />
      </HStack>
    </HStack>
  );
};

export default StandardsPagination;
